/**
 * Start a customer from a global-search address result (RUT-4447).
 *
 * - No customer at the address: the standard New Customer form, prefilled with
 *   the address and its map point.
 * - A customer already lives there: the existing New-owner-at-property flow
 *   (RUT-1455) on that customer. It makes a fresh profile with the property's
 *   measurements and leaves the current customer and their services alone.
 *
 * Rendered outside the search popover so it survives the popover closing.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomerSource, CustomerStatus, PhoneType, getCustomerDisplayName } from '@ruta/shared';
import { CreateCustomerModal } from '@/components/customers/CreateCustomerModal';
import { NewOwnerAtPropertyButton } from '@/components/customers/NewOwnerAtPropertyButton';
import { useCreateCustomerMutation } from '@/hooks/useMutations';
import { toast } from '@/components/ui/use-toast';
import type { Customer, CustomerFormData } from '@/lib/types';
import type { AddressResult } from './AddressResultRow';

/** "4606 Casco Avenue, Edina, Minnesota 55424, United States" → without the country. */
function formAddress(address: AddressResult): string {
  return address.place_name.replace(/,?\s*United States$/i, '').trim();
}

export function newCustomerFormForAddress(address: AddressResult): CustomerFormData {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    phoneType: PhoneType.MOBILE,
    address: formAddress(address),
    // Same [lng, lat] shape CreateCustomerModal stores after geocoding, so it
    // keeps the point the office picked instead of geocoding the text again.
    coordinates: [address.center[0], address.center[1]] as [number, number],
    status: CustomerStatus.PROSPECT,
    source: CustomerSource.MANUAL,
    notes: '',
  } as CustomerFormData;
}

export function SearchCreateCustomerDialog({
  target,
  onClose,
}: {
  target: AddressResult | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const createCustomer = useCreateCustomerMutation();
  const [formData, setFormData] = useState<CustomerFormData | null>(null);

  useEffect(() => {
    setFormData(target && !target.existingCustomer ? newCustomerFormForAddress(target) : null);
    createCustomer.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  if (!target) return null;

  if (target.existingCustomer) {
    const current = {
      id: target.existingCustomer.id,
      displayName: target.existingCustomer.name,
      address: formAddress(target),
    } as unknown as Customer;
    return (
      <NewOwnerAtPropertyButton
        customer={current}
        hideTrigger
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      />
    );
  }

  if (!formData) return null;

  const handleSave = async () => {
    const result = await createCustomer.mutateAsync(formData);
    onClose();
    toast({ title: 'Customer created', description: `${getCustomerDisplayName(result.customer)} was added.` });
    navigate(`/customers/${result.customer.id}`);
  };

  return (
    <CreateCustomerModal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      formData={formData}
      onFormDataChange={(updates) => setFormData((prev) => (prev ? { ...prev, ...updates } : prev))}
      onSave={handleSave}
      saving={createCustomer.isPending}
      error={createCustomer.error?.message || null}
    />
  );
}
